import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { doc, getDoc, updateDoc, writeBatch, serverTimestamp, collection } from 'firebase/firestore';
import { db } from '../firebase';
import { useT } from '../contexts/LocaleContext';
import { LanguageSwitch } from '../components/LanguageSwitch';

export default function ConfirmItinerary() {
  const [params] = useSearchParams();
  const token  = params.get('token');
  const action = params.get('action'); // 'confirmed' | 'declined' (legacy deep-link)

  const [state,    setState]    = useState('loading');
  const [mode,     setMode]     = useState(null);   // 'grouped' | 'legacy'
  const [data,     setData]     = useState(null);
  const [chosen,   setChosen]   = useState(null);   // 'approved' | 'declined' | 'question'
  const [question, setQuestion] = useState('');
  const [errorMsg, setError]    = useState('');
  const t = useT();
  const c = (key, vars) => t(`supplier.confirm.${key}`, vars);

  useEffect(() => {
    if (!token) { setError(c('invalid')); setState('error'); return; }

    (async () => {
      // Try grouped token first
      const groupedSnap = await getDoc(doc(db, 'supplierApprovalTokens', token));
      if (groupedSnap.exists()) {
        const d = groupedSnap.data();
        // Suppliers can't read the tour itself, only their own view of it
        // (functions/supplierViews.js); staff opening the link read the tour.
        const view = d.supplierId
          ? await getDoc(doc(db, 'supplierViews', `${d.supplierId}__${d.requestId}`)).then((s) => (s.exists() ? s.data() : null)).catch(() => null)
          : null;
        let tour;
        let items;
        if (view) {
          tour = view.tour || {};
          items = (d.itemIds || []).map((id) => (view.myItems || []).find((i) => i.id === id) || null);
        } else {
          const tourSnap = await getDoc(doc(db, 'requests', d.requestId));
          tour = tourSnap.exists() ? tourSnap.data() : {};
          items = await Promise.all(
            (d.itemIds || []).map((id) =>
              getDoc(doc(db, 'requests', d.requestId, 'itinerary', id))
                .then((s) => s.exists() ? { id: s.id, ...s.data() } : null)
            )
          );
        }

        const tokenData = {
          mode: 'grouped',
          tokenId: token,
          requestId: d.requestId,
          supplierName: d.supplierName,
          supplierEmail: d.supplierEmail,
          itemIds: d.itemIds || [],
          items: items.filter(Boolean),
          used: d.used || false,
          tour,
        };
        setData(tokenData);
        setMode('grouped');

        // Auto-submit approve/decline from email action links
        if (!d.used && (action === 'approved' || action === 'declined')) {
          const batch = writeBatch(db);
          for (const id of (d.itemIds || [])) {
            batch.update(doc(db, 'requests', d.requestId, 'itinerary', id), {
              approvalStatus: action,
              respondedAt: serverTimestamp(),
            });
          }
          batch.update(doc(db, 'supplierApprovalTokens', token), {
            used: true,
            usedAt: serverTimestamp(),
            response: action,
          });
          await batch.commit();
          setChosen(action);
          setState('done');
          return;
        }

        // Pre-open question textarea from email question link
        if (!d.used && action === 'question') {
          setChosen('question');
        }

        setState('ready');
        return;
      }

      // Fall back to legacy per-item token
      const legacySnap = await getDoc(doc(db, 'itineraryTokens', token));
      if (!legacySnap.exists()) throw new Error(c('invalid'));
      const { requestId, itemId } = legacySnap.data();

      const [itemSnap, tourSnap] = await Promise.all([
        getDoc(doc(db, 'requests', requestId, 'itinerary', itemId)),
        getDoc(doc(db, 'requests', requestId)),
      ]);
      if (!itemSnap.exists()) throw new Error(c('notFound'));

      const itemData = itemSnap.data();
      const tourData = tourSnap.exists() ? tourSnap.data() : {};
      setData({ mode: 'legacy', requestId, itemId, item: { ...itemData, id: itemId }, tour: tourData });
      setMode('legacy');
      setState('ready');

      // Handle legacy deep-link actions (e.g. ?token=x&action=confirmed)
      if (action === 'confirmed' || action === 'declined') {
        await updateDoc(doc(db, 'requests', requestId, 'itinerary', itemId), {
          confirmStatus: action, respondedAt: serverTimestamp(),
        });
        setChosen(action);
        setState('done');
      }
    })().catch((err) => {
      setError(err.message || c('invalid'));
      setState('error');
    });
  }, [token, action]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleGroupedAction(act) {
    setState('submitting');
    setChosen(act);
    try {
      const batch = writeBatch(db);
      const newStatus = act === 'approved' ? 'approved' : act === 'declined' ? 'declined' : 'question';

      for (const itemId of data.itemIds) {
        batch.update(doc(db, 'requests', data.requestId, 'itinerary', itemId), {
          approvalStatus: newStatus,
          respondedAt: serverTimestamp(),
          ...(act === 'question' ? { questionText: question } : {}),
        });
      }
      batch.update(doc(db, 'supplierApprovalTokens', data.tokenId), {
        used: true,
        usedAt: serverTimestamp(),
        response: act,
        ...(act === 'question' ? { question } : {}),
      });
      await batch.commit();
      setState('done');
    } catch (err) {
      setError(err.message || c('failed'));
      setState('error');
    }
  }

  async function handleLegacyAction(act) {
    setState('submitting');
    setChosen(act);
    try {
      await updateDoc(doc(db, 'requests', data.requestId, 'itinerary', data.itemId), {
        confirmStatus: act, respondedAt: serverTimestamp(),
      });
      setState('done');
    } catch (err) {
      setError(err.message || c('failed'));
      setState('error');
    }
  }

  function formatDate(str) {
    if (!str) return '—';
    const [y, m, d] = str.split('-');
    return `${d}/${m}/${y}`;
  }

  const tour = data?.tour || {};

  return (
    <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center p-4">
      <div className="w-full max-w-[520px] bg-white rounded-[16px] border border-[#E5E7EB] overflow-hidden shadow-sm">

        <div className="bg-[#1e40af] px-8 py-5 text-center relative">
          <div className="absolute top-3 right-3"><LanguageSwitch /></div>
          <img
            src="/MST Sports Tours Logo.png"
            alt="MST Sports Tours"
            className="max-h-14 max-w-[200px] w-auto h-auto mx-auto"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        </div>

        <div className="px-8 py-8">

          {state === 'loading' && (
            <p className="text-[14px] text-[#6B7280] text-center">{c('loading')}</p>
          )}

          {state === 'error' && (
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-[#C0392B]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h2 className="text-[18px] font-bold text-[#111827] mb-2">{c('invalidTitle')}</h2>
              <p className="text-[14px] text-[#6B7280]">{errorMsg}</p>
              <p className="text-[13px] text-[#9CA3AF] mt-4">
                {c('contactUs')}
              </p>
            </div>
          )}

          {/* ── Grouped token (new) ── */}
          {state === 'ready' && mode === 'grouped' && data && !data.used && (
            <>
              <h2 className="text-[20px] font-bold text-[#111827] mb-1">{c('requestTitle')}</h2>
              <p className="text-[14px] text-[#6B7280] mb-5">
                {c('requestIntro', {
                  tour: tour.tourName || `${t.value('sport', tour.sport) || ''} — ${tour.destination || ''}`,
                  dates: `${formatDate(tour.departureDate)} – ${formatDate(tour.returnDate)}`,
                })}
              </p>

              <div className="bg-[#F9FAFB] rounded-[10px] border border-[#E5E7EB] overflow-hidden mb-5">
                <div className="px-4 py-3 border-b border-[#E5E7EB] bg-[#F3F4F6]">
                  <p className="text-[11px] font-bold text-[#9CA3AF] uppercase tracking-[0.9px]">{c('yourItems')}</p>
                </div>
                <div className="divide-y divide-[#E5E7EB]">
                  {data.items.map((item) => (
                    <div key={item.id} className="px-4 py-3 flex items-start gap-3">
                      <div className="min-w-[48px] text-center flex-shrink-0">
                        <p className="text-[11px] font-bold text-[#6B7280]">{t('supplier.day', { n: item.dayIdx != null ? item.dayIdx + 1 : item.day })}</p>
                        <p className="text-[12px] font-semibold text-[#374151]">{item.time || '—'}</p>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[13px] font-semibold text-[#111827]">{t.value('title', item.title) || t.value('activity', item.activityType)}</p>
                        {item.notes && <p className="text-[12px] text-[#6B7280] mt-0.5 italic">{item.notes}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {chosen !== 'question' ? (
                <div className="space-y-3">
                  <button
                    onClick={() => handleGroupedAction('approved')}
                    className="w-full py-3 text-[14px] font-semibold text-white bg-[#1A7A4A] hover:bg-[#145e38] rounded-[10px] transition"
                  >
                    {c('approveAll')}
                  </button>
                  <button
                    onClick={() => handleGroupedAction('declined')}
                    className="w-full py-3 text-[14px] font-semibold text-white bg-[#C0392B] hover:bg-[#991b1b] rounded-[10px] transition"
                  >
                    {c('declineAll')}
                  </button>
                  <button
                    onClick={() => setChosen('question')}
                    className="w-full py-3 text-[14px] font-semibold text-[#374151] bg-[#F3F4F6] hover:bg-[#E5E7EB] rounded-[10px] transition"
                  >
                    {t('supplier.approve.question')}
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-[13px] text-[#374151] font-medium">{t('supplier.approve.whatQuestion')}</p>
                  <textarea
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                    rows={3}
                    placeholder={t('supplier.approve.questionPh')}
                    className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-[#1e40af] resize-none"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleGroupedAction('question')}
                      disabled={!question.trim()}
                      className="flex-1 py-2.5 text-[13px] font-semibold text-white bg-[#1e40af] hover:bg-[#1e3a8a] rounded-[8px] transition disabled:opacity-50"
                    >
                      {t('supplier.approve.send')}
                    </button>
                    <button
                      onClick={() => setChosen(null)}
                      className="px-4 py-2.5 text-[13px] text-[#6B7280] hover:text-[#374151]"
                    >
                      {t('supplier.approve.back')}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}

          {state === 'ready' && mode === 'grouped' && data?.used && (
            <div className="text-center">
              <p className="text-[16px] font-semibold text-[#111827] mb-2">{c('usedTitle')}</p>
              <p className="text-[14px] text-[#6B7280]">{c('used')}</p>
            </div>
          )}

          {/* ── Legacy per-item token ── */}
          {state === 'ready' && mode === 'legacy' && data && (
            <>
              <h2 className="text-[20px] font-bold text-[#111827] mb-1">{c('legacyTitle')}</h2>
              <p className="text-[14px] text-[#6B7280] mb-6">{c('legacyIntro')}</p>
              <div className="bg-[#F9FAFB] rounded-[10px] border border-[#E5E7EB] p-4 mb-6 space-y-2.5">
                {[
                  [c('tour'),     `${t.value('sport', tour.sport) || ''} — ${tour.destination || ''}`],
                  [c('dates'),    `${formatDate(tour.departureDate)} – ${formatDate(tour.returnDate)}`],
                  [c('activity'), t.value('title', data.item.title)],
                  [c('day'),      t('supplier.day', { n: data.item.day })],
                  [c('time'),     `${data.item.time || '—'}${data.item.endTime ? ' – ' + data.item.endTime : ''}`],
                  data.item.notes ? [c('notes'), data.item.notes] : null,
                ].filter(Boolean).map(([label, value]) => (
                  <div key={label} className="flex gap-3">
                    <span className="text-[12px] text-[#9CA3AF] font-medium w-20 flex-shrink-0 pt-0.5">{label}</span>
                    <span className="text-[13px] text-[#111827] font-medium">{value}</span>
                  </div>
                ))}
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => handleLegacyAction('confirmed')}
                  className="flex-1 py-3 text-[14px] font-semibold text-white bg-[#1A7A4A] hover:bg-[#145e38] rounded-[10px] transition"
                >
                  {c('confirm')}
                </button>
                <button
                  onClick={() => handleLegacyAction('declined')}
                  className="flex-1 py-3 text-[14px] font-semibold text-white bg-[#C0392B] hover:bg-[#991b1b] rounded-[10px] transition"
                >
                  {c('decline')}
                </button>
              </div>
            </>
          )}

          {state === 'submitting' && (
            <p className="text-[14px] text-[#6B7280] text-center">{c('submitting')}</p>
          )}

          {state === 'done' && (
            <div className="text-center">
              <div className={`w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4 ${chosen === 'approved' || chosen === 'confirmed' ? 'bg-green-50' : chosen === 'question' ? 'bg-amber-50' : 'bg-red-50'}`}>
                {(chosen === 'approved' || chosen === 'confirmed') ? (
                  <svg className="w-7 h-7 text-[#1A7A4A]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                ) : chosen === 'question' ? (
                  <svg className="w-7 h-7 text-[#D97706]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01" />
                  </svg>
                ) : (
                  <svg className="w-7 h-7 text-[#C0392B]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                )}
              </div>
              <h2 className="text-[20px] font-bold text-[#111827] mb-2">
                {chosen === 'approved' || chosen === 'confirmed' ? c('approved') : chosen === 'question' ? c('questionSent') : c('declined')}
              </h2>
              <p className="text-[14px] text-[#6B7280]">
                {(chosen === 'approved' || chosen === 'confirmed')
                  ? c('approvedBody')
                  : chosen === 'question'
                  ? c('questionBody')
                  : c('declinedBody')}
              </p>
            </div>
          )}

        </div>

        <div className="px-8 py-4 bg-[#F9FAFB] border-t border-[#E5E7EB] text-center">
          <p className="text-[12px] text-[#9CA3AF]">MST Sports Tours · msttoursportal.co.uk</p>
        </div>
      </div>
    </div>
  );
}
